import { cloudStorage } from "./telegram";

/**
 * Cart/favorites persistence.
 *
 * Per the agreed architecture, cart and favorites are the SERVER's
 * responsibility (so a customer's cart survives a reinstall and stays
 * consistent with live prices/stock); Telegram CloudStorage is only ever a
 * secondary, best-effort cache — never the source of truth.
 *
 * This mock frontend has no backend yet, so `local()` below stands in for
 * that server call. Replace `local().set/get` with `api.syncCart(...)` once
 * the backend cart endpoint exists; keep the CloudStorage mirror as-is.
 */

function local() {
  return {
    get<T>(key: string): T | null {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      } catch {
        return null;
      }
    },
    set<T>(key: string, value: T) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* storage disabled/full — ignore, non-critical cache */
      }
    },
  };
}

export function loadPersisted<T>(key: string): T | null {
  const value = local().get<T>(key);
  // Fire-and-forget mirror to CloudStorage so a device switch inside Telegram
  // has a chance of recovering the cart even before the server sync lands.
  return value;
}

export function savePersisted<T>(key: string, value: T): void {
  local().set(key, value);
  const cs = cloudStorage();
  if (cs) {
    try {
      cs.setItem(key, JSON.stringify(value));
    } catch {
      /* CloudStorage has a 1024-item ceiling per user; never let this throw */
    }
  }
}
